/* eslint-disable @typescript-eslint/no-explicit-any */
// ReportDialogContext.tsx
import IconCheckWhite from '@assets/icons/check-white.svg?react';
import { Box, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import React, {
 createContext,
 ReactNode,
 useCallback,
 useContext,
 useEffect,
 useMemo,
 useState,
} from 'react';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import CloseButton from '../components/common/CloseButton';
import SelectApiManual from '../components/common/SelectApiManual';
import { reportsGenerateStart } from '../redux/reports/reportsSlice';
import { RootState } from '../redux/store';
import { downloadBase64File } from '../utils/base64File';

// Define types for dialog state and the context function
interface ReportDialogProps {
 id: string | number;
 isOpen: boolean;
 onConfirm: () => void;
}

interface ReportDialogContextType {
 openDialog: ({ id, onConfirm }: Omit<ReportDialogProps, 'isOpen'>) => void;
}

// Create the context with proper types
const ReportDialogContext = createContext<ReportDialogContextType | undefined>(
 undefined,
);

export const ReportDialogProvider = ({ children }: { children: ReactNode }) => {
 const { t } = useTranslation();
 const dispatch = useDispatch();
 const selectorReports = useSelector((state: RootState) => state.reports);
 const { loading, success, report } = selectorReports;

 const [dialogState, setDialogState] = useState<ReportDialogProps>({
  id: -1,
  isOpen: false,
  onConfirm: () => {},
 });

 const openDialog = useCallback(
  ({ id, onConfirm }: Omit<ReportDialogProps, 'isOpen'>) => {
   setDialogState({
    id,
    isOpen: true,
    onConfirm,
   });
  },
  [],
 );

 const closeDialog = useCallback(() => {
  setDialogState((prevState) => ({ ...prevState, isOpen: false }));
 }, []);

 const confirmAction = useCallback(() => {
  if (dialogState.onConfirm) dialogState.onConfirm();
  closeDialog();
 }, [dialogState.onConfirm, closeDialog]);

 // Memoize the value for the context provider
 const contextValue = useMemo(
  () => ({
   openDialog,
  }),
  [openDialog],
 );

 const {
  handleSubmit,
  control,
  formState: { errors },
  setValue,
  register,
  reset,
  watch,
 } = useForm({
  mode: 'onChange',
 });

 const generateReport = (data: FieldValues) => {
  console.log(data);
  dispatch(
   reportsGenerateStart({
    type: 'PdfReport',
    Id: data.Id,
    ReportTemplateId: data.ReportTemplateId.Id,
    Copies: data.Copies,
   }),
  );
  console.log(data);
 };

 useEffect(() => {
  if (success?.reportsGenerate && report) {
   if (report.file && report.type && report.extension && report.name) {
    downloadBase64File(report.file, report.name, report.type, report.extension);
   }
  }
 }, [success]);

 return (
  <ReportDialogContext.Provider value={contextValue}>
   {children}
   {dialogState.isOpen && (
    <Dialog
     open={dialogState.isOpen}
     onClose={closeDialog}
     sx={{
      '& .MuiDialog-container': {
       '& .MuiPaper-root': {
        width: '100%',
        overflowY: 'hidden',
        //  height: '100%',
        maxWidth: '408px', // 960
       },
      },
     }}
    >
     <form onSubmit={handleSubmit(generateReport)}>
      <DialogTitle>{t('global.generateReport')}</DialogTitle>
      <CloseButton closeDialog={closeDialog} />
      <DialogContent sx={{ pt: 0, overflow: 'visible' }}>
       <Box
        sx={{
         display: 'flex',
         // alignItems: 'center',
         gap: 1,
         py: 0,
         width: '100%',
        }}
       >
        <input type="hidden" {...register('Id')} value={dialogState.id} />
        <SelectApiManual
         required
         size="medium"
         name="ReportTemplateId"
         url="/api/Reports/v1"
         filterVar="Name"
         fixedFilters={[
          {
           field: 'DocumentType',
           operator: 'eq',
           value: 'Single',
          },
         ]}
         isEditable
         label="Ataskaitos šablonas"
         control={control}
         setValue={setValue}
         disabled={false}
        />
        <Controller
         name="Copies"
         control={control}
         rules={{ required: 'Copies is required' }}
         defaultValue="1"
         render={({ field }) => (
          <TextField
           sx={{ width: '200px' }}
           {...field}
           value={field.value || ''}
           label="Kopijos"
           variant="outlined"
           error={!!errors?.[field.name]}
           helperText={
            errors?.[field.name]
             ? errors?.[field.name]?.message?.toString()
             : ''
           }
           fullWidth
          />
         )}
        />
       </Box>
      </DialogContent>
      <DialogActions disableSpacing sx={{ gap: 2 }}>
       <Button
        onClick={closeDialog}
        variant="text"
        color="secondary"
        disabled={loading?.reportsGenerate || false}
       >
        {t('global.cancel')}
       </Button>
       <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={loading?.reportsGenerate || Object.keys(errors).length > 0}
        endIcon={<IconCheckWhite style={{ width: 16, height: 11 }} />}
        //  sx={{ marginLeft: '0 !important' }}
       >
        {t('global.generate')}
       </Button>
      </DialogActions>
     </form>
    </Dialog>
   )}
  </ReportDialogContext.Provider>
 );
};

export const useReportDialog = (): ReportDialogContextType => {
 const context = useContext(ReportDialogContext);
 if (!context) {
  throw new Error('useReportDialog must be used within a ReportDialogProvider');
 }
 return context;
};
